/* eslint-disable arrow-body-style */
import {
    Autocomplete,
    Box,
    Divider,
    Grid2 as Grid,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Typography,
    FormControl,
    FormHelperText
} from '@mui/material';
import React, { useEffect } from 'react';
import BackAction from 'components/reusable/BackAction';
import { GLOBAL_DATE_FORMAT, THERAPY_MAX_TIME, THERAPY_MIN_TIME } from 'constant';

import { AspyreDatePicker as DatePicker, AspyreTimePicker as TimePicker } from 'components/common/InputFields';
import { useAssessmentForm } from './useAssessmentForm';
import ESign from 'components/ESign';
import { LoadingButton } from '@mui/lab';
import Spinner from 'ui-component/Spinner';
import moment from 'moment';

export const getEmployeeName = (employee) => {
    if (!employee) return '';

    // eslint-disable-next-line no-nested-ternary
    const name = employee.name
        ? employee.name
        : employee.first_name && employee.last_name
          ? `${employee.first_name} ${employee.last_name}`
          : '';
    const credentials = employee.credentials ? `, ${employee.credentials}` : '';
    const npi = employee.npi ? ` (${employee.npi})` : '';
    return `${name}${credentials}${npi}`;
};

const AssessmentForm = ({ AssessmentId }) => {
    const {
        formik,
        studentData,
        studentIsLoading,
        studentIsFetching,
        locations,
        locationFetching,
        locationLoading,
        therapistList,
        therapyFetching,
        therapyLoading,
        diagnoses,
        assessmentTypes,
        activities,

        assessmentFetching,
        assessmentLoading,
        activityFetching,
        activityLoading,

        diagnosesFetching,
        diagnosesLoading,
        isSaving,

        detailFetching,
        detailLoading,
        handleEsign,
        isSigning,
        detail,
        user,
        isPsychologist,
        isTherapist,
        handleSearchChange,
        handleStudentChange
    } = useAssessmentForm({ AssessmentId });

    useEffect(() => {
        if ((isPsychologist || isTherapist) && therapistList?.fields?.length > 0 && user?.emp_id) {
            // Find therapist by matching emp_id
            const matchedTherapist = therapistList.fields.find((t) => t.emp_id === user.emp_id);

            if (matchedTherapist) {
                formik.setFieldValue('employee', matchedTherapist);
            }
        }
    }, [isPsychologist, isTherapist, user?.emp_id, therapistList?.fields]);

    return (
        <>
            {(detailFetching || detailLoading || isSaving || isSigning) && <Spinner />}
            <Grid container spacing={4} sx={{ mt: 0.25 }}>
                {/* Basic Details */}
                <Grid size={12}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                        Basic Details
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Autocomplete
                                options={studentData?.fields?.filter((patient) => patient?.user?.is_active) || []}
                                disabled={AssessmentId}
                                loading={studentIsLoading || studentIsFetching}
                                getOptionLabel={(option) => option?.user?.name}
                                value={formik.values.student}
                                onInputChange={(e, value) => {
                                    handleSearchChange(value); // Trigger API call on input change
                                }}
                                onChange={(e, value) => {
                                    handleStudentChange(value);
                                }}
                                onBlur={() => formik.setFieldTouched('student', true)}
                                filterOptions={(x) => x} // Disable client-side filtering since API handles it
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Patient *"
                                        placeholder="Select a patient"
                                        error={formik.touched.student && Boolean(formik.errors.student)}
                                        helperText={formik.touched.student && formik.errors.student}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Autocomplete
                                options={locations?.fields || []}
                                loading={locationFetching || locationLoading}
                                getOptionLabel={(option) => option?.location?.name}
                                value={formik.values?.location}
                                onChange={(e, value) => {
                                    formik.setFieldValue('location', value);
                                    if (!isPsychologist && !isTherapist) {
                                        formik.setFieldValue('employee', null);
                                    }
                                }}
                                onBlur={() => formik.setFieldTouched('location', true)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Location *"
                                        placeholder="Select a location"
                                        error={Boolean(formik.touched.location && formik.errors.location)}
                                        helperText={formik.touched.location && formik.errors.location}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Autocomplete
                                options={therapistList?.fields || []}
                                loading={therapyFetching || therapyLoading}
                                disabled={isPsychologist || isTherapist}
                                getOptionLabel={(option) => getEmployeeName(option)}
                                value={formik.values.employee}
                                onChange={(e, value) => formik.setFieldValue('employee', value)}
                                onBlur={() => formik.setFieldTouched('employee', true)}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        {getEmployeeName(option)}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Employee *"
                                        placeholder="Select an employee"
                                        error={formik.touched.employee && Boolean(formik.errors.employee)}
                                        helperText={formik.touched.employee && formik.errors.employee}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Session Information */}
                <Grid size={{ xs: 12, md: 12 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                        Session Information
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Autocomplete
                                options={diagnoses?.fields || []}
                                loading={diagnosesFetching || diagnosesLoading}
                                getOptionLabel={(option) => `${option.icd_code} - ${option.description}`}
                                value={formik.values.diagnosis}
                                onChange={(e, value) => formik.setFieldValue('diagnosis', value)}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Diagnosis *"
                                        placeholder="Select a diagnosis"
                                        size="medium"
                                        error={Boolean(formik.touched.diagnosis && formik.errors.diagnosis)}
                                        helperText={formik.touched.diagnosis && formik.errors.diagnosis}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Date/Time Section */}
                <Grid size={{ xs: 12, md: 10 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                        Date & Time
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                value={formik.values.session.date}
                                onChange={(value) => {
                                    formik.setFieldValue('session.date', value);
                                }}
                                required
                                label="Date"
                                inputFormat={GLOBAL_DATE_FORMAT}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        required
                                        onBlur={() => formik.setFieldTouched('session.date', true)}
                                        error={Boolean(formik.touched.session?.date && formik.errors.session?.date)}
                                        helperText={formik.touched.session?.date && formik.errors.session?.date}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                            <TimePicker
                                required
                                fullWidth
                                format="hh:mm A"
                                label="Start Time"
                                minTime={THERAPY_MIN_TIME}
                                maxTime={THERAPY_MAX_TIME}
                                value={formik.values.session.startTime}
                                onChange={(value) => formik.setFieldValue('session.startTime', value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(formik.touched.session?.startTime && formik.errors.session?.startTime)}
                                        helperText={formik.touched.session?.startTime && formik.errors.session?.startTime}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                            <TimePicker
                                required
                                fullWidth
                                format="hh:mm A"
                                label="End Time"
                                minTime={THERAPY_MIN_TIME}
                                maxTime={THERAPY_MAX_TIME}
                                value={formik.values.session.endTime}
                                onChange={(value) => formik.setFieldValue('session.endTime', value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(formik.touched.session?.endTime && formik.errors.session?.endTime)}
                                        helperText={formik.touched.session?.endTime && formik.errors.session?.endTime}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Assessment Section */}
                <Grid size={12}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                        Assessment
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                filterSelectedOptions
                                loading={assessmentFetching || assessmentLoading}
                                options={assessmentTypes?.fields || []}
                                getOptionLabel={(option) => option.name}
                                value={formik.values.assessmentType}
                                onChange={(e, value) => formik.setFieldValue('assessmentType', value)}
                                onBlur={() => formik.setFieldTouched('assessmentType', true)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Assessment Type *"
                                        placeholder="Select an assessment type"
                                        size="medium"
                                        error={Boolean(formik.touched.assessmentType && formik.errors.assessmentType)}
                                        helperText={formik.touched.assessmentType && formik.errors.assessmentType}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1, // reduce gap
                                    height: '100%'
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500, color: 'text.primary', minWidth: 60 }} // reduce width
                                >
                                    Qualified?
                                </Typography>

                                <FormControl
                                    error={Boolean(formik.touched.qualified && formik.errors.qualified)}
                                    component="fieldset"
                                    sx={{ m: 0 }} // remove extra margin if any
                                >
                                    <RadioGroup
                                        row
                                        value={formik.values.qualified}
                                        onChange={(e) => formik.setFieldValue('qualified', e.target.value)}
                                    >
                                        <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                                        <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                                    </RadioGroup>

                                    {formik.touched.qualified && formik.errors.qualified && (
                                        <FormHelperText>{formik.errors.qualified}</FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Activity Section */}
                <Grid size={12}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                        Activity
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                filterSelectedOptions
                                options={activities?.fields || []}
                                loading={activityFetching || activityLoading}
                                getOptionLabel={(option) => option.name}
                                value={formik.values.activity}
                                autoFocus={false} // ← Add this
                                disablePortal={false} // ← Ensure dropdown renders in portal
                                onChange={(e, value) => formik.setFieldValue('activity', value)}
                                onBlur={() => formik.setFieldTouched('activity', true)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Activity *"
                                        placeholder="Select an activity"
                                        size="medium"
                                        error={Boolean(formik.touched.activity && formik.errors.activity)}
                                        helperText={formik.touched.activity && formik.errors.activity}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Notes Section */}
                <Grid size={12}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                        Notes
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Session Notes"
                                multiline
                                rows={6}
                                fullWidth
                                placeholder="Enter session notes here..."
                                value={formik.values.notes}
                                onChange={(e) => formik.setFieldValue('notes', e.target.value)}
                                onBlur={() => formik.setFieldTouched('notes', true)}
                                error={Boolean(formik.touched.notes && formik.errors.notes)}
                                helperText={formik.touched.notes && formik.errors.notes}
                                sx={{ mb: 3 }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container sx={{ justifyContent: detail?.fields?.is_full_signed ? 'center' : 'start' }}>
                <div className="e-sign">
                    {detail?.fields?.is_full_signed && (
                        <div className="sign-info">
                            <p>Signed by</p>
                            <p>
                                <b>
                                    {detail?.fields?.signed_therapist_emp?.name || ''}
                                    {', '}
                                    {detail?.fields?.signed_therapist_emp?.credentials || ''}
                                </b>{' '}
                                on <b>{moment.utc(detail?.fields?.therapist_sign_date).local().format(`${GLOBAL_DATE_FORMAT} hh:mm A`)}</b>
                            </p>
                        </div>
                    )}
                </div>
                {AssessmentId && !detail?.fields?.is_full_signed && detail?.fields?.employee?.id === user?.emp_id && (
                    <ESign
                        renderButton={(props) => (
                            <LoadingButton loading={isSaving || isSigning} variant="contained" color="secondary" {...props}>
                                E-Sign
                            </LoadingButton>
                        )}
                        onChange={(password) => handleEsign(password, AssessmentId)}
                    />
                )}
            </Grid>
            <BackAction
                sx={{ mt: 4 }}
                link="/psychologicaltesting"
                actionElement={
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/*
                         * 1. Check if the user is on the update page.
                         * 2. Ensure the record is not fully signed.
                         * 3. Verify the assigned therapist matches the logged-in user.
                         */}

                        {!detail?.fields?.is_full_signed && (
                            <LoadingButton
                                type="submit"
                                loading={isSaving}
                                variant="contained"
                                color="primary"
                                onClick={(e) => {
                                    formik.submitForm();
                                }}
                            >
                                Save
                            </LoadingButton>
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default AssessmentForm;
